import React, { useState } from 'react';
import { X, ExternalLink } from 'lucide-react';

const AnnouncementBanner = ({ announcement, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible || !announcement) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss(announcement.id);
  };

  // Get styling from bannerStyle or use defaults
  const bannerStyle = announcement.bannerStyle || {};
  const backgroundColor = bannerStyle.backgroundColor || '#10b981';
  const textColor = bannerStyle.textColor || '#ffffff';
  const gradientColor = bannerStyle.gradientColor || '#059669';
  const styleType = bannerStyle.styleType || 'solid';
  const icon = bannerStyle.icon || '';
  const boldTitle = bannerStyle.boldTitle !== false;

  // Build the style object
  const getContainerStyle = () => {
    const style = { color: textColor };
    
    if (styleType === 'gradient') {
      style.background = `linear-gradient(to right, ${backgroundColor}, ${gradientColor})`;
    } else if (styleType === 'bordered') {
      style.background = 'rgba(255, 255, 255, 0.95)';
      style.border = `2px solid ${backgroundColor}`;
      style.color = backgroundColor;
    } else {
      style.background = backgroundColor;
    }
    
    return style;
  };

  const containerStyle = getContainerStyle();
  const isBordered = styleType === 'bordered';

  return (
    <div 
      className="py-3 px-4 relative"
      style={containerStyle}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Image - shows first if available */}
          {announcement.imageUrl && (
            <img
              src={announcement.imageUrl}
              alt=""
              className="w-10 h-10 rounded object-cover flex-shrink-0"
            />
          )}
          
          {/* Icon/Emoji - shows after image or alone */}
          {icon && (
            <span className="text-xl flex-shrink-0">{icon}</span>
          )}
          
          {/* Content */}
          <div className="flex-1">
            <p className="text-sm md:text-base">
              <span className={boldTitle ? 'font-semibold' : 'font-normal'}>
                {announcement.title}
              </span>
              {announcement.content && (
                <span className={`ml-2 ${isBordered ? 'opacity-80' : 'opacity-90'}`}>
                  {announcement.content}
                </span>
              )}
            </p>
          </div>
          
          {/* Link Button */}
          {announcement.linkUrl && (
            <a
              href={announcement.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors flex-shrink-0 ${
                isBordered 
                  ? 'bg-gray-100 hover:bg-gray-200' 
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              style={isBordered ? { color: backgroundColor } : {}}
            >
              {announcement.linkText || 'Learn More'}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        
        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          className={`p-1 rounded-full transition-colors flex-shrink-0 ${
            isBordered 
              ? 'hover:bg-gray-100' 
              : 'hover:bg-white/20'
          }`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
